import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Spinner, Disclaimer, UpgradeGate } from "@/components/shared";
import { AskQuestionDialog } from "@/components/AskQuestionDialog";
import { aaLabel, AA_CATEGORIES, AA_STATUS_STYLE } from "@/lib/askAnything";
import { MessageCircleHeart, Plus, Inbox, Clock, ChevronRight, Sparkles, Lock } from "lucide-react";

const TABS = [
  { key: "open", label: "Open", match: (t) => t.status !== "closed" },
  { key: "replied", label: "KidPath replied", match: (t) => t.status === "sent" },
  { key: "waiting_kidpath", label: "Waiting for KidPath", match: (t) => ["new", "assigned", "drafting"].includes(t.status) },
  { key: "waiting_you", label: "Waiting for you", match: (t) => t.status === "waiting_on_user" },
  { key: "closed", label: "Closed", match: (t) => t.status === "closed" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

function ThreadCard({ t }) {
  return (
    <Link to={`/app/ask/${t.id}`} className="kid-card p-4 flex items-center gap-4 hover:border-kid-teal transition-colors" data-testid="aa-thread-card">
      <div className="w-10 h-10 rounded-xl bg-kid-skytint text-kid-teal grid place-items-center shrink-0"><MessageCircleHeart size={18} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-kid-navy truncate">{t.title}</p>
          <span className={`text-[.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${AA_STATUS_STYLE[t.user_status] || "bg-kid-mist text-kid-muted"}`}>{t.user_status}</span>
        </div>
        <p className="text-sm text-kid-muted truncate mt-0.5">{t.last_message_preview || aaLabel(AA_CATEGORIES, t.category)}</p>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs text-kid-muted">{fmt(t.updated_at)}</div>
        <ChevronRight size={16} className="text-kid-line ml-auto mt-1" />
      </div>
    </Link>
  );
}

export default function AskAnything() {
  const [access, setAccess] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    const { data: acc } = await api.get("/ask-anything/access");
    setAccess(acc);
    if (acc.access !== "locked") {
      const { data } = await api.get("/ask-anything/threads");
      setThreads(data.threads);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Spinner />;

  if (access.access === "locked") {
    return (
      <div className="max-w-2xl kid-stagger">
        <PageHeader eyebrow="Ask Anything" title="A private place to ask, share & hear back" />
        <div className="kid-card p-8 text-center" data-testid="aa-locked">
          <div className="w-14 h-14 rounded-2xl bg-kid-skytint text-kid-teal grid place-items-center mx-auto mb-4"><Lock size={26} /></div>
          <h3 className="text-xl font-extrabold text-kid-navy">Ask Anything is included with Family Path</h3>
          <p className="text-kid-muted mt-2 max-w-md mx-auto">Questions, worries, wins, tough days, small victories — paid KidPath families can send private questions to KidPath and receive a thoughtful reply within 72 hours.</p>
          <Link to="/app/account" className="kid-btn kid-btn-primary h-12 px-7 mt-6 inline-flex" data-testid="aa-upgrade-cta"><Sparkles size={17} /> Upgrade to Family Path</Link>
        </div>
      </div>
    );
  }

  const readOnly = access.access === "read_only";
  const filtered = threads.filter(TABS.find((t) => t.key === tab).match);

  return (
    <div className="max-w-3xl kid-stagger">
      <PageHeader eyebrow="Ask Anything" title="Ask Anything"
        subtitle="Questions. Worries. Wins. Tough days. Small victories. Whatever's on your mind, this is a safe place to ask, share, and hear back from KidPath." />

      {/* Hero / how it works */}
      <div className="kid-card p-6 mb-5 bg-kid-navy text-white" data-testid="aa-home-hero">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-extrabold">How it works</h3>
            <ol className="text-sm text-white/80 mt-2 space-y-1 list-decimal pl-5">
              <li>Send us your question, worry, win, or update.</li>
              <li>KidPath reviews it carefully.</li>
              <li>You receive a thoughtful reply within 72 hours.</li>
            </ol>
          </div>
          {!readOnly && (
            <button onClick={() => setDialogOpen(true)} disabled={!access.can_create}
              className="kid-btn kid-btn-primary h-12 px-6 disabled:opacity-50" data-testid="aa-new-question">
              <Plus size={18} /> Ask a question
            </button>
          )}
        </div>
        {!readOnly && !access.can_create && (
          <p className="text-xs text-white/70 mt-3" data-testid="aa-limit-note">You have {access.open_threads} of {access.max_open_threads} open conversations. Close one to start a new question.</p>
        )}
        {readOnly && <p className="text-xs text-white/70 mt-3" data-testid="aa-readonly-note">Your subscription has ended — you have read-only access to past conversations for 30 days.</p>}
      </div>

      {/* Inbox */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" data-testid="aa-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`aa-tab-${t.key}`}
            className={`text-sm font-poppins font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${tab === t.key ? "bg-kid-teal text-white" : "bg-white border border-kid-line text-kid-muted hover:text-kid-teal"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="kid-card p-10 text-center" data-testid="aa-empty">
          <Inbox size={28} className="mx-auto mb-3 text-kid-muted opacity-60" />
          <h4 className="font-bold text-kid-navy">Nothing here yet</h4>
          <p className="text-sm text-kid-muted mt-1 max-w-sm mx-auto">When something comes up — a question, worry, tough day, confusing form, school conversation, or small win — you can share it here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="aa-thread-list">
          {filtered.map((t) => <ThreadCard key={t.id} t={t} />)}
        </div>
      )}

      <div className="mt-6"><Disclaimer>KidPath provides general information and organization support only. Please confirm important decisions with official sources and qualified professionals.</Disclaimer></div>

      <AskQuestionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={() => { setDialogOpen(false); load(); }} />
    </div>
  );
}
